import _ from "lodash";
import React from "react";
import {
  StudentShareOutlined,
  CalendarDateOutlined,
  ClipboardPencilLinesOutlined,
  CommentSquareLeftClassroomOutlined,
  ClipboardTickOutlined,
  MinusCircleOutlined,
  LockOutlined,
  LockOpenOutlined,
  CalculatorOutlined,
  ClockTimerOutlined,
  DeviceMobileDisableOutlined,
  CalendarScheduleOutlined,
  UnitPlanOutlined,
  GridOutlined,
  AssessmentOutlined,
  PlaceholderFileOutlined,
  GoalsOutlined,
  TickCircleOutlined,
  WarningCircleOutlined,
  StudentPendingOutlined,
  UserArrowOutlined,
  UserCloseOutlined,
  StudentEvaluateOutlined,
  ReSubmissionRequestOutlined,
  CommentOutlined,
  EvaluationNotApplicableOutlined,
  AssessmentApplicableOutlined,
  DashedCircleMinusOutlined,
  AssessmentAddOutlined,
  JournalAddOutlined,
  ClockAlarmOutlined,
  SwitchArrowsOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  RefreshArrowOutlined,
  StarsTwoFilled,
  TickCircleOpenOutlined,
  ClockOutlined,
  ClockArrowsOutlined,
  PauseFilled,
  ClockRecentsOutlined,
  GlobeOutlined,
} from "@toddle-edu/ds-icons";
import { colors } from "@toddle-edu/ds-theme";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { NORMAL_MESSAGE_ASSIGNMENT_ALERT_TYPES } from "Utils/MessageHelpers";
import { pypElementMapping } from "Constants/plannerElementMappings";
import { getDurationForTimedTest } from "Worksheet/modules/WorksheetUtils";
import { formatDate } from "Utils";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import {
  getDateMomentObject,
  checkIsMomentIsoString,
} from "Utils/DateTimeHelpers";
import {
  TAGGABLE_PYP_ELEMENTS_V2,
  TAGGABLE_DP_ELEMENTS_V2,
  TAGGABLE_MYP_ELEMENTS_V2,
  TAGGABLE_UBD_ELEMENTS,
} from "Constants/taggableElements";
import { CURRICULUM_TYPE_UBD, VISIBLE } from "Constants/stringConstants";
import {
  ASSESSMENT_TOOL_ICON_MAPPING,
  OVERRIDE_STATUS_OPTIONS,
} from "ClassRoom/modules/Constants";
import { getIsEnabledExcusedStatusInAssignment } from "Utils/ACLStoreUtils";

const COMMENT_TOOL_ADDED_LABEL = {
  staff: "assessment_evaluation_tool_comment_added_staff_message",
  otherStaff: "assessment_evaluation_tool_comment_added_generic_message",
  student: "assessment_evaluation_tool_comment_added_generic_message",
  parent: "assessment_evaluation_tool_comment_added_generic_message",
};

/** -- Assignment activity label and icon mapping object --
 * generic structure for each activity itemType
  [itemType]: {
     icon: <IconComponent />,
     message: {
       staff: staff_message_locale_key,
       otherStaff: other_staff_message_locale_key,
       student: student_message_locale_key,
       parent: parent_message_locale_key,
     },
     iconBgColor: "", [optional - default: colors.surfaceSubtle]
     bgColor: "", [optional - default: colors.surfaceWhite]
     fontColor: "" [optional - default: colors.textsubtle]
  } 
 */

const ASSIGNMENT_ACTIVITY_TYPES = {
  // Assign settings activity types
  ASSIGNMENT_ASSIGNED: {
    icon: <StudentShareOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_assigned_staff_message",
      otherStaff: "assignment_assigned_other_staff_message",
      student: "assignment_assigned_student_message",
      parent: "assignment_assigned_parent_message",
    },
  },
  ASSIGNMENT_ASSIGNED_BACKDATED: {
    icon: <StudentShareOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_assigned_backdated_staff_message",
      otherStaff: "assignment_assigned_backdated_generic_message",
      student: "assignment_assigned_backdated_generic_message",
      parent: "assignment_assigned_backdated_generic_message",
    },
  },
  ASSIGNMENT_DUE_DATE_UPDATED: {
    icon: <CalendarDateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_due_date_updated_staff_message",
      otherStaff: "assignment_due_date_updated_generic_message",
      student: "assignment_due_date_updated_generic_message",
      parent: "assignment_due_date_updated_generic_message",
    },
  },
  ASSIGNMENT_DUE_DATE_ADDED: {
    icon: <CalendarDateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_due_date_added_staff_message",
      otherStaff: "assignment_due_date_added_generic_message",
      student: "assignment_due_date_added_generic_message",
      parent: "assignment_due_date_added_generic_message",
    },
  },
  ASSIGNMENT_DUE_DATE_REMOVED: {
    icon: <CalendarDateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_due_date_removed_staff_message",
      otherStaff: "assignment_due_date_removed_generic_message",
      student: "assignment_due_date_removed_generic_message",
      parent: "assignment_due_date_removed_generic_message",
    },
  },
  ASSIGNMENT_SUBMISSION_SETTING_ENABLED: {
    icon: (
      <ClipboardPencilLinesOutlined variant={"subtle"} size={"xxx-small"} />
    ),
    message: {
      staff: "assignment_submission_setting_enabled_staff_message",
      otherStaff: "assignment_submission_setting_enabled_generic_message",
      student: "assignment_submission_setting_enabled_generic_message",
      parent: "assignment_submission_setting_enabled_generic_message",
    },
  },
  ASSIGNMENT_SUBMISSION_SETTING_DISABLED: {
    icon: (
      <ClipboardPencilLinesOutlined variant={"subtle"} size={"xxx-small"} />
    ),
    message: {
      staff: "assignment_submission_setting_disabled_staff_message",
      otherStaff: "assignment_submission_setting_disabled_generic_message",
      student: "assignment_submission_setting_disabled_generic_message",
      parent: "assignment_submission_setting_disabled_generic_message",
    },
  },
  ASSIGNMENT_CLASS_DISCUSSION_ENABLED: {
    icon: (
      <CommentSquareLeftClassroomOutlined
        variant={"subtle"}
        size={"xxx-small"}
      />
    ),
    message: {
      staff: "assignment_class_discussion_enabled_staff_message",
      otherStaff: "assignment_class_discussion_enabled_generic_message",
      student: "assignment_class_discussion_enabled_generic_message",
      parent: "assignment_class_discussion_enabled_generic_message",
    },
  },
  ASSIGNMENT_CLASS_DISCUSSION_DISABLED: {
    icon: (
      <CommentSquareLeftClassroomOutlined
        variant={"subtle"}
        size={"xxx-small"}
      />
    ),
    message: {
      staff: "assignment_class_discussion_disabled_staff_message",
      otherStaff: "assignment_class_discussion_disabled_generic_message",
      student: "assignment_class_discussion_disabled_generic_message",
      parent: "assignment_class_discussion_disabled_generic_message",
    },
  },
  ASSIGNMENT_SIMILARITY_CHECK_ENABLED: {
    icon: <ClipboardTickOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_similarity_check_enabled_staff_message",
      otherStaff: "assignment_similarity_check_enabled_generic_message",
      student: "assignment_similarity_check_enabled_generic_message",
      parent: "assignment_similarity_check_enabled_generic_message",
    },
  },
  ASSIGNMENT_SIMILARITY_CHECK_DISABLED: {
    icon: <ClipboardTickOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_similarity_check_disabled_staff_message",
      otherStaff: "assignment_similarity_check_disabled_generic_message",
      student: "assignment_similarity_check_disabled_generic_message",
      parent: "assignment_similarity_check_disabled_generic_message",
    },
  },
  ASSIGNMENT_FINAL_GRADE_CALCULATION_EXCLUDED: {
    icon: <MinusCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_final_grade_calculation_excluded_staff_message",
      otherStaff: "assignment_final_grade_calculation_excluded_generic_message",
      student: "assignment_final_grade_calculation_excluded_generic_message",
      parent: "assignment_final_grade_calculation_excluded_generic_message",
    },
  },
  ASSIGNMENT_FINAL_GRADE_CALCULATION_INCLUDED: {
    icon: <MinusCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_final_grade_calculation_included_staff_message",
      otherStaff: "assignment_final_grade_calculation_included_generic_message",
      student: "assignment_final_grade_calculation_included_generic_message",
      parent: "assignment_final_grade_calculation_included_generic_message",
    },
  },
  ASSIGNMENT_LOCK_AFTER_DUE_DATE_ENABLED: {
    icon: <LockOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_lock_after_due_date_enabled_staff_message",
      otherStaff: "assignment_lock_after_due_date_enabled_generic_message",
      student: "assignment_lock_after_due_date_enabled_generic_message",
      parent: "assignment_lock_after_due_date_enabled_generic_message",
    },
  },
  ASSIGNMENT_LOCK_AFTER_DUE_DATE_DISABLED: {
    icon: <LockOpenOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_lock_after_due_date_disabled_staff_message",
      otherStaff: "assignment_lock_after_due_date_disabled_generic_message",
      student: "assignment_lock_after_due_date_disabled_generic_message",
      parent: "assignment_lock_after_due_date_disabled_generic_message",
    },
  },
  ASSIGNMENT_ON_SCREEN_CALCULATOR_ENABLED: {
    icon: <CalculatorOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_on_screen_calculator_enabled_staff_message",
      otherStaff: "assignment_on_screen_calculator_enabled_generic_message",
      student: "assignment_on_screen_calculator_enabled_generic_message",
      parent: "assignment_on_screen_calculator_enabled_generic_message",
    },
  },
  ASSIGNMENT_ON_SCREEN_CALCULATOR_DISABLED: {
    icon: <CalculatorOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_on_screen_calculator_disabled_staff_message",
      otherStaff: "assignment_on_screen_calculator_disabled_generic_message",
      student: "assignment_on_screen_calculator_disabled_generic_message",
      parent: "assignment_on_screen_calculator_disabled_generic_message",
    },
  },
  ASSIGNMENT_TIMED_TEST_ENABLED: {
    icon: <ClockTimerOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_timed_test_enabled_staff_message",
      otherStaff: "assignment_timed_test_enabled_generic_message",
      student: "assignment_timed_test_enabled_generic_message",
      parent: "assignment_timed_test_enabled_generic_message",
    },
  },
  ASSIGNMENT_TIMED_TEST_DISABLED: {
    icon: <ClockTimerOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_timed_test_disabled_staff_message",
      otherStaff: "assignment_timed_test_disabled_generic_message",
      student: "assignment_timed_test_disabled_generic_message",
      parent: "assignment_timed_test_disabled_generic_message",
    },
  },
  ASSIGNMENT_TIMED_TEST_UPDATED: {
    icon: <ClockTimerOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_timed_test_updated_staff_message",
      otherStaff: "assignment_timed_test_updated_generic_message",
      student: "assignment_timed_test_updated_generic_message",
      parent: "assignment_timed_test_updated_generic_message",
    },
  },
  ASSIGNMENT_LOCK_SCREEN_ENABLED: {
    icon: <DeviceMobileDisableOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_locked_screen_enabled_staff_message",
      otherStaff: "assignment_locked_screen_enabled_generic_message",
      student: "assignment_locked_screen_enabled_generic_message",
      parent: "assignment_locked_screen_enabled_generic_message",
    },
  },
  ASSIGNMENT_LOCK_SCREEN_DISABLED: {
    icon: <DeviceMobileDisableOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_locked_screen_disabled_staff_message",
      otherStaff: "assignment_locked_screen_disabled_generic_message",
      student: "assignment_locked_screen_disabled_generic_message",
      parent: "assignment_locked_screen_disabled_generic_message",
    },
  },
  ASSIGNMENT_CLOSE_SUBMISSION_DATE_UPDATED: {
    icon: <CalendarDateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_close_submission_date_updated_staff_message",
      otherStaff: "assignment_close_submission_date_updated_generic_message",
      student: "assignment_close_submission_date_updated_generic_message",
      parent: "assignment_close_submission_date_updated_generic_message",
    },
  },
  ASSIGNMENT_CLOSE_SUBMISSION_DATE_REMOVED: {
    icon: <CalendarDateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_close_submission_date_removed_staff_message",
      otherStaff: "assignment_close_submission_date_removed_generic_message",
      student: "assignment_close_submission_date_removed_generic_message",
      parent: "assignment_close_submission_date_removed_generic_message",
    },
  },
  ASSIGNMENT_CLOSE_SUBMISSION_DATE_ADDED: {
    icon: <CalendarDateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_close_submission_date_added_staff_message",
      otherStaff: "assignment_close_submission_date_added_generic_message",
      student: "assignment_close_submission_date_added_generic_message",
      parent: "assignment_close_submission_date_added_generic_message",
    },
  },
  ASSIGNMENT_VISIBILITY_UPDATED: {
    icon: <EyeOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_visibility_updated_staff_message",
      otherStaff: "assignment_visibility_updated_generic_message",
      student: "assignment_visibility_updated_generic_message",
      parent: "assignment_visibility_updated_generic_message",
    },
  },
  ASSIGNMENT_RESCHEDULED: {
    icon: <CalendarDateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_open_date_updated_staff_message",
      otherStaff: "assignment_open_date_updated_generic_message",
      student: "assignment_open_date_updated_generic_message",
      parent: "assignment_open_date_updated_generic_message",
    },
  },

  // Submission/Evaluation activity types
  STUDENT_ASSIGNMENT_SUBMISSION_LOCKED: {
    icon: <LockOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_submission_locked_staff_message",
      otherStaff: "student_assignment_submission_locked_other_staff_message",
      student: "student_assignment_submission_locked_generic_message",
      parent: "student_assignment_submission_locked_generic_message",
    },
  },
  ASSIGNMENT_SUBMISSION_LOCKED: {
    icon: <LockOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_submission_locked_staff_message",
      otherStaff: "student_assignment_submission_locked_generic_message",
      student: "student_assignment_submission_locked_generic_message",
      parent: "student_assignment_submission_locked_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_AUTO_LOCKED: {
    icon: <LockOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_submission_auto_locked_generic_message",
      otherStaff: "student_assignment_submission_auto_locked_generic_message",
      student: "student_assignment_submission_auto_locked_generic_message",
      parent: "student_assignment_submission_auto_locked_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_UNLOCKED: {
    icon: <LockOpenOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_submission_unlocked_staff_message",
      otherStaff: "student_assignment_submission_unlocked_other_staff_message",
      student: "student_assignment_submission_unlocked_generic_message",
      parent: "student_assignment_submission_unlocked_generic_message",
    },
  },
  ASSIGNMENT_SUBMISSION_UNLOCKED: {
    icon: <LockOpenOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assignment_submission_unlocked_staff_message",
      otherStaff: "student_assignment_submission_unlocked_generic_message",
      student: "student_assignment_submission_unlocked_generic_message",
      parent: "student_assignment_submission_unlocked_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATED: {
    icon: <TickCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_evaluated_staff_message_v2",
      otherStaff: "student_assignment_evaluated_generic_message_v2",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATION_PENDING: {
    icon: <WarningCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_evaluation_pending_staff_message",
      otherStaff: "student_assignment_evaluation_pending_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_COMPLETE: {
    icon: <TickCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_complete_staff_message_v2",
      otherStaff: "student_assignment_complete_generic_message_v2",
      student: "student_assignment_complete_generic_message_v2",
      parent: "student_assignment_complete_generic_message_v2",
    },
  },
  STUDENT_ASSIGNMENT_INCOMPLETE: {
    icon: <StudentPendingOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_incomplete_staff_message",
      otherStaff: "student_assignment_incomplete_generic_message",
      student: "student_assignment_incomplete_generic_message",
      parent: "student_assignment_incomplete_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATION_SHARED: {
    icon: <UserArrowOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_evaluation_shared_staff_message",
      otherStaff: "student_assignment_evaluation_shared_other_staff_message",
      student: "student_assignment_evaluation_shared_generic_message",
      parent: "student_assignment_evaluation_shared_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATION_UNSHARED: {
    icon: <UserCloseOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_evaluation_unshared_staff_message",
      otherStaff: "student_assignment_evaluation_unshared_generic_message",
      student: "student_assignment_evaluation_unshared_generic_message",
      parent: "student_assignment_evaluation_unshared_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATION_UPDATED: {
    icon: (
      <ClipboardPencilLinesOutlined variant={"subtle"} size={"xxx-small"} />
    ),
    message: {
      staff: "student_assignment_evaluation_updated_staff_message",
      otherStaff: "student_assignment_evaluation_updated_generic_message",
      student: "student_assignment_evaluation_updated_student_message",
      parent: "student_assignment_evaluation_updated_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATION_SHARED_AND_EVALUATED: {
    icon: <UserArrowOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_evaluation_shared_and_evaluated_staff_message",
      otherStaff:
        "student_assignment_evaluation_shared_and_evaluated_other_staff_message",
      student: "student_assignment_evaluation_shared_generic_message",
      parent: "student_assignment_evaluation_shared_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_DONE: {
    icon: <StudentEvaluateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_submission_done_generic_message",
      otherStaff: "student_assignment_submission_done_generic_message",
      student: "student_assignment_submission_done_student_message",
      parent: "student_assignment_submission_done_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_WORKSHEET_SUBMITTED: {
    icon: <StudentEvaluateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_worksheet_submitted_generic_message",
      otherStaff: "student_assignment_worksheet_submitted_generic_message",
      student: "student_assignment_worksheet_submitted_student_message",
      parent: "student_assignment_worksheet_submitted_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_RETURNED: {
    icon: <ReSubmissionRequestOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_submission_returned_staff_message",
      otherStaff: "student_assignment_submission_returned_generic_message",
      student: "student_assignment_submission_returned_generic_message",
      parent: "student_assignment_submission_returned_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_RETURNED_SUBMISSION_SUBMITTED: {
    icon: <StudentEvaluateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_submission_done_generic_message",
      otherStaff: "student_assignment_submission_done_generic_message",
      student: "student_assignment_submission_done_student_message",
      parent: "student_assignment_submission_done_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_WORKSHEET_RESUBMISSION_SUBMITTED: {
    icon: <StudentEvaluateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_worksheet_submitted_generic_message",
      otherStaff: "student_assignment_worksheet_submitted_generic_message",
      student: "student_assignment_worksheet_submitted_student_message",
      parent: "student_assignment_worksheet_submitted_generic_message",
    },
  },
  UPDATED_STUDENT_ASSIGNMENT_FILE: {
    icon: <CommentOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "updated_student_assignment_file_staff_message",
      otherStaff: "updated_student_assignment_file_generic_message",
      student: "updated_student_assignment_file_student_message",
      parent: "updated_student_assignment_file_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATION_NOT_APPLICABLE: {
    icon: (
      <EvaluationNotApplicableOutlined variant={"subtle"} size={"xxx-small"} />
    ),
    message: {
      staff: "student_assignment_evaluation_not_applicable_staff_message_v2",
      otherStaff:
        "student_assignment_evaluation_not_applicable_other_staff_message_v2",
      student: "student_assignment_evaluation_not_applicable_student_message",
      parent: "student_assignment_evaluation_not_applicable_parent_message",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATION_APPLICABLE: {
    icon: (
      <AssessmentApplicableOutlined variant={"subtle"} size={"xxx-small"} />
    ),
    message: {
      staff: "student_assignment_evaluation_applicable_staff_message",
      otherStaff:
        "student_assignment_evaluation_applicable_other_staff_message",
      student: "student_assignment_evaluation_applicable_student_message",
      parent: "student_assignment_evaluation_applicable_parent_message",
    },
  },
  STUDENT_ASSIGNMENT_EXCUSED: {
    icon: (
      <EvaluationNotApplicableOutlined variant={"subtle"} size={"xxx-small"} />
    ),
    message: {
      staff: "student_assignment_excused_staff_message",
      otherStaff: "student_assignment_excused_other_staff_message",
      student: "student_assignment_excused_student_message",
      parent: "student_assignment_excused_parent_message",
    },
  },
  STUDENT_ASSIGNMENT_REMOVED_FROM_EXCUSED: {
    icon: (
      <AssessmentApplicableOutlined variant={"subtle"} size={"xxx-small"} />
    ),
    message: {
      staff: "student_assignment_removed_from_excused_staff_message",
      otherStaff: "student_assignment_removed_from_excused_other_staff_message",
      student: "student_assignment_removed_from_excused_student_message",
      parent: "student_assignment_removed_from_excused_parent_message",
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_DRAFT: {
    icon: <StudentPendingOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_submission_unsubmitted_generic_message",
      otherStaff: "student_submission_unsubmitted_generic_message",
      student: "student_submission_unsubmitted_student_message",
      parent: "student_submission_unsubmitted_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_FILE_REMOVED: {
    icon: <DashedCircleMinusOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      actionByStaff: {
        staff: "staff_removed_student_assignment_submission_file_staff_message",
        otherStaff:
          "staff_removed_student_assignment_submission_file_generic_message",
        student:
          "staff_removed_student_assignment_submission_file_student_message",
        parent:
          "staff_removed_student_assignment_submission_file_generic_message",
      },
      actionByStudent: {
        staff: "student_assignment_submission_file_removed_generic_message",
        otherStaff:
          "student_assignment_submission_file_removed_generic_message",
        student: "student_assignment_submission_file_removed_student_message",
        parent: "student_assignment_submission_file_removed_generic_message",
      },
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_FILE_ADDED: {
    icon: <AssessmentAddOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      actionByStaff: {
        staff: "staff_added_student_assignment_submission_file_staff_message",
        otherStaff:
          "staff_added_student_assignment_submission_file_generic_message",
        student:
          "staff_added_student_assignment_submission_file_student_message",
        parent:
          "staff_added_student_assignment_submission_file_generic_message",
      },
      actionByStudent: {
        staff: "student_assignment_submission_file_added_generic_message",
        otherStaff: "student_assignment_submission_file_added_generic_message",
        student: "student_assignment_submission_file_added_student_message",
        parent: "student_assignment_submission_file_added_generic_message",
      },
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_WORKBOOK_ADDED: {
    icon: <AssessmentAddOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      actionByStaff: {
        staff:
          "staff_added_student_assignment_submission_workbook_staff_message",
        otherStaff:
          "staff_added_student_assignment_submission_workbook_generic_message",
        student:
          "staff_added_student_assignment_submission_workbook_student_message",
        parent:
          "staff_added_student_assignment_submission_workbook_generic_message",
      },
      actionByStudent: {
        staff: "student_assignment_submission_workbook_added_generic_message",
        otherStaff:
          "student_assignment_submission_workbook_added_generic_message",
        student: "student_assignment_submission_workbook_added_student_message",
        parent: "student_assignment_submission_workbook_added_generic_message",
      },
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_WORKBOOK_REMOVED: {
    icon: <MinusCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      actionByStaff: {
        staff:
          "staff_removed_student_assignment_submission_workbook_staff_message",
        otherStaff:
          "staff_removed_student_assignment_submission_workbook_generic_message",
        student:
          "staff_removed_student_assignment_submission_workbook_student_message",
        parent:
          "staff_removed_student_assignment_submission_workbook_generic_message",
      },
      actionByStudent: {
        staff: "student_assignment_submission_workbook_removed_generic_message",
        otherStaff:
          "student_assignment_submission_workbook_removed_generic_message",
        student:
          "student_assignment_submission_workbook_removed_student_message",
        parent:
          "student_assignment_submission_workbook_removed_generic_message",
      },
    },
  },
  STUDENT_ASSIGNMENT_WORK_TO_PORTFOLIO: {
    icon: <JournalAddOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      actionByStaff: {
        staff: "staff_added_student_assignment_work_to_portfolio_staff_message",
        otherStaff:
          "staff_added_student_assignment_work_to_portfolio_generic_message",
        student:
          "staff_added_student_assignment_work_to_portfolio_student_message",
        parent:
          "staff_added_student_assignment_work_to_portfolio_generic_message",
      },
      actionByStudent: {
        staff: "student_assignment_work_to_portfolio_generic_message",
        otherStaff: "student_assignment_work_to_portfolio_generic_message",
        student: "student_assignment_work_to_portfolio_student_message",
        parent: "student_assignment_work_to_portfolio_generic_message",
      },
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_TO_PORTFOLIO: {
    icon: <JournalAddOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      actionByStaff: {
        staff:
          "staff_added_student_assignment_submission_to_portfolio_staff_message",
        otherStaff:
          "staff_added_student_assignment_submission_to_portfolio_generic_message",
        student:
          "staff_added_student_assignment_submission_to_portfolio_student_message",
        parent:
          "staff_added_student_assignment_submission_to_portfolio_generic_message",
      },
      actionByStudent: {
        staff: "student_assignment_submission_to_portfolio_generic_message",
        otherStaff:
          "student_assignment_submission_to_portfolio_generic_message",
        student: "student_assignment_submission_to_portfolio_student_message",
        parent: "student_assignment_submission_to_portfolio_generic_message",
      },
    },
  },
  STUDENT_ASSIGNMENT_PDF_ANNOTED: {
    icon: <CommentOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_pdf_annoted_staff_message",
      otherStaff: "student_assignment_pdf_annoted_generic_message",
      student: "student_assignment_pdf_annoted_student_message",
      parent: "student_assignment_pdf_annoted_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_TEMPLATE_SUBMISSION_ADDED: {
    icon: <AssessmentAddOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_template_submission_added_generic_message",
      otherStaff:
        "student_assignment_template_submission_added_generic_message",
      student: "student_assignment_template_submission_added_student_message",
      parent: "student_assignment_template_submission_added_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_SUBMISSION_FILE_FEEDBACK: {
    icon: <CommentOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_submission_file_feedback_staff_message",
      otherStaff: "student_assignment_submission_file_feedback_generic_message",
      student: "student_assignment_submission_file_feedback_student_message",
      parent: "student_assignment_submission_file_feedback_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_OPENED: {
    icon: <EyeOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_opened_generic_message",
      otherStaff: "student_assignment_opened_generic_message",
      student: "student_assignment_opened_student_message",
      parent: "student_assignment_opened_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_START_AI_CONVERSATION_SESSION: {
    icon: <PlayCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_start_ai_conversation_session_generic_message",
      otherStaff:
        "student_assignment_start_ai_conversation_session_generic_message",
      student:
        "student_assignment_start_ai_conversation_session_student_message",
      parent:
        "student_assignment_start_ai_conversation_session_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_RESTART_AI_CONVERSATION_SESSION: {
    icon: <RefreshArrowOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff:
        "student_assignment_restart_ai_conversation_session_generic_message",
      otherStaff:
        "student_assignment_restart_ai_conversation_session_generic_message",
      student:
        "student_assignment_restart_ai_conversation_session_student_message",
      parent:
        "student_assignment_restart_ai_conversation_session_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_END_AI_CONVERSATION_SESSION: {
    icon: <TickCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_end_ai_conversation_session_generic_message",
      otherStaff:
        "student_assignment_end_ai_conversation_session_generic_message",
      student: "student_assignment_end_ai_conversation_session_student_message",
      parent: "student_assignment_end_ai_conversation_session_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_SUBMIT_AI_CONVERSATION_SESSION: {
    icon: <StudentEvaluateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff:
        "student_assignment_submitted_ai_conversation_session_generic_message",
      otherStaff:
        "student_assignment_submitted_ai_conversation_session_generic_message",
      student:
        "student_assignment_submitted_ai_conversation_session_student_message",
      parent:
        "student_assignment_submitted_ai_conversation_session_generic_message",
    },
  },
  AI_EVALUATION_TOGGLE_ACTIVITY: {
    icon: (
      <StarsTwoFilled
        style={{ color: "var(--interactive-tertiary)" }}
        size={"xxx-small"}
      />
    ),
    iconBgColor: "var(--decorative-background-subtle-violet)",
    message: {
      staff: "ai_evaluation_toggle_activity_staff_message",
      otherStaff: "ai_evaluation_toggle_activity_generic_message",
    },
  },
  AI_EVALUATION_REGENERATE_FOR_TOOL_ACTIVITY: {
    icon: (
      <RefreshArrowOutlined
        style={{ color: "var(--interactive-tertiary)" }}
        size={"xxx-small"}
      />
    ),
    iconBgColor: "var(--decorative-background-subtle-violet)",
    message: {
      staff: "ai_evaluation_regenerate_for_tool_activity_staff_message",
      otherStaff: "ai_evaluation_regenerate_for_tool_activity_generic_message",
    },
  },
  AI_EVALUATION_REGENERATE_ACTIVITY: {
    icon: (
      <RefreshArrowOutlined
        style={{ color: "var(--interactive-tertiary)" }}
        size={"xxx-small"}
      />
    ),
    iconBgColor: "var(--decorative-background-subtle-violet)",
    message: {
      staff: "ai_evaluation_regenerate_activity_staff_message",
      otherStaff: "ai_evaluation_regenerate_activity_generic_message",
    },
  },
  AI_EVALUATION_ASSESSMENT_TOOL_APPROVE_ACTIVITY: {
    icon: (
      <TickCircleOpenOutlined
        style={{ color: "var(--interactive-tertiary)" }}
        size={"xxx-small"}
      />
    ),
    iconBgColor: "var(--decorative-background-subtle-violet)",
    message: {
      staff: "ai_evaluation_assessment_tool_approve_activity_staff_message",
      otherStaff:
        "ai_evaluation_assessment_tool_approve_activity_generic_message",
    },
  },
  AI_EVALUATION_QUICK_APPROVE_ACTIVITY: {
    icon: (
      <StarsTwoFilled
        style={{ color: "var(--interactive-tertiary)" }}
        size={"xxx-small"}
      />
    ),
    iconBgColor: "var(--decorative-background-subtle-violet)",
    message: {
      staff: "ai_evaluation_quick_approve_activity_staff_message",
      otherStaff: "ai_evaluation_quick_approve_activity_generic_message",
    },
  },

  // Edit assessment(of assignment) activity types
  ASSESSMENT_TERM_UPDATED: {
    icon: <CalendarScheduleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_term_updated_staff_message",
      otherStaff: "assessment_term_updated_generic_message",
      student: "assessment_term_updated_generic_message",
      parent: "assessment_term_updated_generic_message",
    },
  },
  ASSESSMENT_UNIT_UPDATED: {
    icon: <UnitPlanOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_unit_updated_staff_message",
      otherStaff: "assessment_unit_updated_generic_message",
      student: "assessment_unit_updated_generic_message",
      parent: "assessment_unit_updated_generic_message",
    },
  },
  ASSESSMENT_CATEGORY_UPDATED: {
    icon: <GridOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_category_updated_staff_message",
      otherStaff: "assessment_category_updated_generic_message",
      student: "assessment_category_updated_generic_message",
      parent: "assessment_category_updated_generic_message",
    },
  },
  ASSESSMENT_CATEGORY_REMOVED: {
    icon: <GridOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_category_removed_staff_message",
      otherStaff: "assessment_category_removed_generic_message",
      student: "assessment_category_removed_generic_message",
      parent: "assessment_category_removed_generic_message",
    },
  },
  ASSESSMENT_CATEGORY_ADDED: {
    icon: <GridOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_category_added_staff_message",
      otherStaff: "assessment_category_added_generic_message",
      student: "assessment_category_added_generic_message",
      parent: "assessment_category_added_generic_message",
    },
  },
  ASSESSMENT_SUBMISSION_TEMPLATE_ADDED: {
    icon: <AssessmentOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_submission_template_added_staff_message",
      otherStaff: "assessment_submission_template_added_generic_message",
      student: "assessment_submission_template_added_generic_message",
      parent: "assessment_submission_template_added_generic_message",
    },
  },
  ASSESSMENT_SUBMISSION_TEMPLATE_REMOVED: {
    icon: <AssessmentOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_submission_template_removed_staff_message",
      otherStaff: "assessment_submission_template_removed_generic_message",
      student: "assessment_submission_template_removed_generic_message",
      parent: "assessment_submission_template_removed_generic_message",
    },
  },
  ASSESSMENT_SUBMISSION_TEMPLATE_UPDATED: {
    icon: <AssessmentOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_submission_template_updated_staff_message",
      otherStaff: "assessment_submission_template_updated_generic_message",
      student: "assessment_submission_template_updated_generic_message",
      parent: "assessment_submission_template_updated_generic_message",
    },
  },
  ASSESSMENT_RESOURCE_ADDED: {
    icon: <PlaceholderFileOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_resource_added_staff_message",
      otherStaff: "assessment_resource_added_generic_message",
      student: "assessment_resource_added_generic_message",
      parent: "assessment_resource_added_generic_message",
    },
  },
  ASSESSMENT_RESOURCE_REMOVED: {
    icon: <PlaceholderFileOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_resource_removed_staff_message",
      otherStaff: "assessment_resource_removed_generic_message",
      student: "assessment_resource_removed_generic_message",
      parent: "assessment_resource_removed_generic_message",
    },
  },
  ASSESSMENT_EVALUATION_TOOL_ADDED: {
    icon: ASSESSMENT_TOOL_ICON_MAPPING,
    message: {
      staff: "assessment_evaluation_tool_added_staff_message",
      otherStaff: "assessment_evaluation_tool_added_generic_message",
      student: "assessment_evaluation_tool_added_generic_message",
      parent: "assessment_evaluation_tool_added_generic_message",
    },
  },
  ASSESSMENT_EVALUATION_TOOL_UPDATED: {
    icon: ASSESSMENT_TOOL_ICON_MAPPING,
    message: {
      staff: "assessment_evaluation_tool_updated_staff_message",
      otherStaff: "assessment_evaluation_tool_updated_generic_message",
      student: "assessment_evaluation_tool_updated_generic_message",
      parent: "assessment_evaluation_tool_updated_generic_message",
    },
  },
  ASSESSMENT_EVALUATION_TOOL_REMOVED: {
    icon: ASSESSMENT_TOOL_ICON_MAPPING,
    message: {
      staff: "assessment_evaluation_tool_removed_staff_message",
      otherStaff: "assessment_evaluation_tool_removed_generic_message",
      student: "assessment_evaluation_tool_removed_generic_message",
      parent: "assessment_evaluation_tool_removed_generic_message",
    },
  },
  ASSESSMENT_LEARNING_GOALS_ADDED: {
    icon: <GoalsOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_learning_goals_added_staff_message",
      otherStaff: "assessment_learning_goals_added_generic_message",
      student: "assessment_learning_goals_added_generic_message",
      parent: "assessment_learning_goals_added_generic_message",
    },
  },
  ASSESSMENT_LEARNING_GOALS_UPDATED: {
    icon: <GoalsOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_learning_goals_updated_staff_message_v2",
      otherStaff: "assessment_learning_goals_updated_generic_message_v2",
      student: "assessment_learning_goals_updated_generic_message_v2",
      parent: "assessment_learning_goals_updated_generic_message_v2",
    },
  },
  ASSESSMENT_LEARNING_GOALS_REMOVED: {
    icon: <GoalsOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_learning_goals_removed_staff_message_v2",
      otherStaff: "assessment_learning_goals_removed_generic_message_v2",
      student: "assessment_learning_goals_removed_generic_message_v2",
      parent: "assessment_learning_goals_removed_generic_message_v2",
    },
  },
  ASSESSMENT_RATE_ON_LEARNING_GOALS_ENABLED: {
    icon: <GoalsOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_rate_on_learning_goals_enabled_staff_message",
      otherStaff: "assessment_rate_on_learning_goals_enabled_generic_message",
      student: "assessment_rate_on_learning_goals_enabled_generic_message",
      parent: "assessment_rate_on_learning_goals_enabled_generic_message",
    },
  },
  ASSESSMENT_RATE_ON_LEARNING_GOALS_DISABLED: {
    icon: <GoalsOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_rate_on_learning_goals_disabled_staff_message",
      otherStaff: "assessment_rate_on_learning_goals_disabled_generic_message",
      student: "assessment_rate_on_learning_goals_disabled_generic_message",
      parent: "assessment_rate_on_learning_goals_disabled_generic_message",
    },
  },
  ASSESSMENT_TYPE_UPDATED: {
    icon: <SwitchArrowsOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "assessment_type_updated_staff_message",
      otherStaff: "assessment_type_updated_generic_message",
      student: "assessment_type_updated_generic_message",
      parent: "assessment_type_updated_generic_message",
    },
  },

  // * Worksheet activity logs - triggered from worksheet monitoring and student
  WORKSHEET_STARTED: {
    icon: <ClipboardTickOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "worksheet_started_generic_message",
      otherStaff: "worksheet_started_generic_message",
      student: "worksheet_started_student_message",
      parent: "worksheet_started_generic_message",
    },
  },
  WORKSHEET_PAUSE: {
    icon: <PauseFilled variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "worksheet_paused_staff_message",
      otherStaff: "worksheet_paused_generic_message",
      student: "worksheet_paused_generic_message",
      parent: "worksheet_paused_generic_message",
    },
  },
  WORKSHEET_UNPAUSE: {
    icon: <PlayCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "worksheet_resumed_staff_message",
      otherStaff: "worksheet_resumed_generic_message",
      student: "worksheet_resumed_generic_message",
      parent: "worksheet_resumed_generic_message",
    },
  },
  WORKSHEET_REOPEN: {
    icon: <ClockRecentsOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "worksheet_reopen_staff_message",
      otherStaff: "worksheet_reopen_generic_message",
      student: "worksheet_reopen_generic_message",
      parent: "worksheet_reopen_generic_message",
    },
  },
  WORKSHEET_ADD_EXTRA_TIME: {
    icon: <ClockOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "worksheet_added_extra_time_staff_message",
      otherStaff: "worksheet_added_extra_time_generic_message",
      student: "worksheet_added_extra_time_generic_message",
      parent: "worksheet_added_extra_time_generic_message",
    },
  },
  WORKSHEET_REMOVE_ALL_EXTRA_TIME: {
    icon: <ClockArrowsOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "worksheet_remove_extra_time_staff_message",
      otherStaff: "worksheet_remove_extra_time_generic_message",
      student: "worksheet_remove_extra_time_generic_message",
      parent: "worksheet_remove_extra_time_generic_message",
    },
  },
  WORKSHEET_ON_SCREEN_CALCULATOR: {
    icon: <CalculatorOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "on_screen_calculator_staff_message",
      otherStaff: "on_screen_calculator_generic_message",
      student: "on_screen_calculator_generic_message",
      parent: "on_screen_calculator_generic_message",
    },
  },
  WORKSHEET_BROWSER_LOCK_SCREEN: {
    icon: <GlobeOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "browser_lock_staff_message",
      otherStaff: "browser_lock_generic_message",
      student: "browser_lock_generic_message",
      parent: "browser_lock_generic_message",
    },
  },
  OVERRIDE_SUBMISSION_STATUS: {
    icon: <StudentEvaluateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "override_submission_status_staff_message",
      otherStaff: "override_submission_status_other_staff_message",
      student: "override_submission_status_student_message",
      parent: "override_submission_status_parent_message",
    },
  },
  RESET_OVERRIDE_SUBMISSION_STATUS: {
    icon: <StudentEvaluateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "reset_override_submission_status_staff_message",
      otherStaff: "reset_override_submission_status_other_staff_message",
      student: "reset_override_submission_status_student_message",
      parent: "reset_override_submission_status_parent_message",
    },
  },
};

/**
 * This obj is to handle old assignment activities where message type is "NORMAL"
 * To avoid data porting, show new activity message UI for these types
 */
const ASSIGNMENT_ACTIVITY_OLD_TYPES = {
  STUDENT_ASSIGNMENT_SUBMISSION: {
    icon: <StudentEvaluateOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_submission_done_generic_message",
      otherStaff: "student_assignment_submission_done_generic_message",
      student: "student_assignment_submission_done_student_message",
      parent: "student_assignment_submission_done_generic_message",
    },
  },
  UNHAND_STUDENT_ASSIGNMENT_SUBMISSION: {
    icon: <ReSubmissionRequestOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_submission_returned_staff_message",
      otherStaff: "student_assignment_submission_returned_generic_message",
      student: "student_assignment_submission_returned_generic_message",
      parent: "student_assignment_submission_returned_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATED: {
    icon: <TickCircleOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_evaluated_staff_message_v2",
      otherStaff: "student_assignment_evaluated_generic_message_v2",
    },
  },
  STUDENT_ASSIGNMENT_SHARED: {
    icon: <UserArrowOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_evaluation_shared_staff_message",
      otherStaff: "student_assignment_evaluation_shared_other_staff_message",
      student: "student_assignment_evaluation_shared_generic_message",
      parent: "student_assignment_evaluation_shared_generic_message",
    },
  },
  STUDENT_ASSIGNMENT_EVALUATED_SHARED: {
    icon: <UserArrowOutlined variant={"subtle"} size={"xxx-small"} />,
    message: {
      staff: "student_assignment_evaluation_shared_and_evaluated_staff_message",
      otherStaff:
        "student_assignment_evaluation_shared_and_evaluated_other_staff_message",
      student: "student_assignment_evaluation_shared_generic_message",
      parent: "student_assignment_evaluation_shared_generic_message",
    },
  },
};

const ALL_ASSIGNMENT_ACTIVITY_TYPES = {
  ...ASSIGNMENT_ACTIVITY_TYPES,
  ...ASSIGNMENT_ACTIVITY_OLD_TYPES,
};

const getMessageKeyAndTokensBasedOnUserType = ({
  itemType,
  currentUserId,
  currentUserType,
  createdBy,
  studentData,
}) => {
  const activityObj = _.get(ALL_ASSIGNMENT_ACTIVITY_TYPES, itemType, {});
  const createdByName = getFormattedUserName({ user: createdBy });
  const studentName = getFormattedUserName({
    user: studentData,
    type: "student",
  });

  const messageObjKey =
    _.get(createdBy, "type", "staff") === "staff"
      ? "actionByStaff"
      : "actionByStudent";

  const messageObj = _.get(
    activityObj,
    `message.${messageObjKey}`,
    _.get(activityObj, "message", {})
  );

  let messageKey = "";
  let tokens = {};
  let messageUserKey = "";

  switch (currentUserType) {
    case "staff": {
      if (currentUserId == createdBy?.id) {
        messageKey = _.get(messageObj, "staff", "");
        tokens = { student: studentName };
        messageUserKey = "staff";
      } else {
        messageKey = _.get(messageObj, "otherStaff", "");
        tokens = { staff: createdByName, student: studentName };
        messageUserKey = "otherStaff";
      }
      break;
    }

    case "student": {
      messageKey = _.get(messageObj, "student", "");
      if (currentUserId != createdBy?.id) {
        tokens = { staff: createdByName };
      }
      messageUserKey = "student";
      break;
    }

    default: {
      messageKey = _.get(messageObj, "parent", "");
      messageUserKey = "parent";
      tokens = { staff: createdByName, student: studentName };
    }
  }

  return { messageKey, tokens, messageUserKey };
};

const getModifiedItemTypes = ({ itemType }) => {
  switch (itemType) {
    case "STUDENT_ASSIGNMENT_EVALUATION_NOT_APPLICABLE":
      return getIsEnabledExcusedStatusInAssignment()
        ? "STUDENT_ASSIGNMENT_EXCUSED"
        : itemType;
    case "STUDENT_ASSIGNMENT_EVALUATION_APPLICABLE":
      return getIsEnabledExcusedStatusInAssignment()
        ? "STUDENT_ASSIGNMENT_REMOVED_FROM_EXCUSED"
        : itemType;
    default:
      return itemType;
  }
};

export const getAssignmentActivityObj = ({
  t,
  currentUserId,
  currentUserType,
  message,
  studentData,
  plannerElementSets,
  curriculumProgramType,
  getCurrentAssessmentTypes,
}) => {
  const { itemTypeV2, createdBy, metadata = {} } = message || {};
  const itemType = getModifiedItemTypes({ itemType: itemTypeV2 });
  const activityObj = _.get(ALL_ASSIGNMENT_ACTIVITY_TYPES, itemType, {});
  const {
    messageKey,
    tokens,
    messageUserKey,
  } = getMessageKeyAndTokensBasedOnUserType({
    itemType,
    currentUserId,
    currentUserType,
    createdBy,
    studentData,
  });
  const messageBuild = _.get(metadata, "messageBuild", {});

  // Below is generic logic to convert any date ISO string coming in metadata -> messageBuild object...
  // to formatted date string to display in activity message
  const messageBuildTokens = _.reduce(
    messageBuild,
    (messageBuildTokens, value, key) => {
      if (
        checkIsMomentIsoString({ dateString: value }) &&
        !_.includes(
          ["WORKSHEET_ADD_EXTRA_TIME", "WORKSHEET_REOPEN"],
          itemTypeV2
        ) // skip for monitoring events
      ) {
        return {
          ...messageBuildTokens,
          date: formatDate({
            date: getDateMomentObject(value),
            format: "Do MMM",
          }),
        };
      }
      return { ...messageBuildTokens, [key]: value };
    },
    {}
  );

  switch (itemType) {
    case "ASSESSMENT_EVALUATION_TOOL_ADDED":
    case "ASSESSMENT_EVALUATION_TOOL_UPDATED":
    case "ASSESSMENT_EVALUATION_TOOL_REMOVED": {
      const toolType = _.get(messageBuildTokens, "toolType", "");
      let toolLabelLocale = _.get(
        _.find(ASSESSMENT_TOOLS, ({ type } = {}) => {
          return type === toolType;
        }),
        "locale"
      );
      if (
        toolType === "SCORE" &&
        itemType !== "ASSESSMENT_EVALUATION_TOOL_REMOVED"
      ) {
        toolLabelLocale = "classRoom:max_score";
      }
      const iconObj = _.get(activityObj, "icon", {});
      const toolIcon = _.get(iconObj, toolType, "");

      let finalMessageKey = messageKey;
      if (
        itemType === "ASSESSMENT_EVALUATION_TOOL_ADDED" &&
        toolType === "COMMENT"
      ) {
        finalMessageKey = COMMENT_TOOL_ADDED_LABEL[messageUserKey];
      }

      return {
        ...activityObj,
        icon: toolIcon,
        label: t(`activity:${finalMessageKey}`, {
          ...tokens,
          toolLabel: t("common:lowercase", { text: t(toolLabelLocale) }),
        }),
      };
    }

    case "ASSIGNMENT_TIMED_TEST_ENABLED":
    case "ASSIGNMENT_TIMED_TEST_UPDATED": {
      const duration = _.get(messageBuildTokens, "duration", 0);
      if (_.isEmpty(duration)) {
        return {
          ...activityObj,
          label: t(`activity:${messageKey}`, {
            ...tokens,
          }),
        };
      }

      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          duration: getDurationForTimedTest({
            t,
            durationInSeconds: _.toNumber(duration) * 60,
          }),
        }),
      };
    }

    case "ASSESSMENT_LEARNING_GOALS_ADDED":
    case "ASSESSMENT_LEARNING_GOALS_UPDATED":
    case "ASSESSMENT_LEARNING_GOALS_REMOVED": {
      const fields = [
        ..._.get(messageBuildTokens, "addedFields", []),
        ..._.get(messageBuildTokens, "removedFields", []),
        ..._.get(messageBuildTokens, "updatedFields", []),
      ];
      const fieldKey = _.first(fields);
      const label = getLearningGoalsLabelByFieldUid({
        fieldKey,
        plannerElementSets,
        curriculumProgramType,
        t,
      });

      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          label: t("common:lowercase", { text: label }),
        }),
      };
    }

    case "ASSESSMENT_TYPE_UPDATED": {
      const assessmentType = _.get(messageBuildTokens, "assessmentType", "le");
      const currentAssessmentTypes = getCurrentAssessmentTypes();
      const { label, localeLabel } =
        _.get(currentAssessmentTypes, assessmentType) || {};
      const assessmentTypeLabel = localeLabel ? t(localeLabel) : label;
      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          newTaskType: assessmentTypeLabel,
        }),
      };
    }

    case "ASSIGNMENT_VISIBILITY_UPDATED": {
      const visibility = _.get(messageBuildTokens, "visibility", VISIBLE);
      const visibilityLabel = t(`activity:${_.toLower(visibility)}`);

      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          visibility: visibilityLabel,
        }),
      };
    }

    case "AI_EVALUATION_TOGGLE_ACTIVITY": {
      const toggleActivity = _.get(messageBuildTokens, "toggle");
      const toggleActivityLabel = t(`activity:${_.toLower(toggleActivity)}`);

      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          toggleActivity: toggleActivityLabel,
        }),
      };
    }

    case "AI_EVALUATION_ASSESSMENT_TOOL_APPROVE_ACTIVITY":
    case "AI_EVALUATION_REGENERATE_FOR_TOOL_ACTIVITY": {
      const toolType = _.get(messageBuildTokens, "toolType", "");
      const toolLabelLocale = _.get(
        _.find(ASSESSMENT_TOOLS, ({ type } = {}) => {
          return type === toolType;
        }),
        "locale"
      );

      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          toolLabel: t("common:lowercase", { text: t(toolLabelLocale) }),
        }),
      };
    }

    case "WORKSHEET_REOPEN":
    case "WORKSHEET_ADD_EXTRA_TIME": {
      const duration = _.get(messageBuildTokens, "duration", 0);
      if (_.isEmpty(duration)) {
        return {
          ...activityObj,
          label: t(`activity:${messageKey}`, { ...tokens }),
        };
      }

      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          duration: getDurationForTimedTest({
            t,
            durationInSeconds: _.toNumber(duration),
          }),
        }),
      };
    }

    case "WORKSHEET_BROWSER_LOCK_SCREEN":
    case "WORKSHEET_ON_SCREEN_CALCULATOR": {
      const isEnabled = _.get(messageBuildTokens, "isEnabled", false);
      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          status: isEnabled ? t("common:enabled") : t("common:disabled"),
        }),
      };
    }

    case "OVERRIDE_SUBMISSION_STATUS":
    case "RESET_OVERRIDE_SUBMISSION_STATUS": {
      const toStatusOption = _.find(OVERRIDE_STATUS_OPTIONS, {
        value: _.get(messageBuildTokens, "overrideStatus"),
      });
      const fromStatusOption = _.find(OVERRIDE_STATUS_OPTIONS, {
        value: _.get(messageBuildTokens, "previousStatus"),
      });
      const toStatus = toStatusOption
        ? t(toStatusOption.localeLabel)
        : _.get(messageBuildTokens, "overrideStatus", "");
      const fromStatus = fromStatusOption
        ? t(fromStatusOption.localeLabel)
        : _.get(messageBuildTokens, "previousStatus", "");
      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          toStatus,
          fromStatus,
        }),
      };
    }

    default:
      return {
        ...activityObj,
        label: t(`activity:${messageKey}`, {
          ...tokens,
          ...messageBuildTokens,
        }),
      };
  }
};

export const getAssignmentMessageObj = params => {
  const { message } = params;
  const { itemTypeV2: itemType } = message || {};

  if (_.includes(NORMAL_MESSAGE_ASSIGNMENT_ALERT_TYPES, itemType)) {
    return {
      ...message,
      icon: <ClockAlarmOutlined variant={"warning"} size={"xxx-small"} />,
      iconBgColor: colors.yellow900,
      bgColor: colors.surfaceWarning,
      fontColor: colors.textDefault,
    };
  }
  return getAssignmentActivityObj(params);
};

const getLearningGoalsLabelByFieldUid = ({
  fieldKey,
  plannerElementSets,
  curriculumProgramType,
  t,
}) => {
  const allTaggableElements = [
    ...TAGGABLE_DP_ELEMENTS_V2,
    ...TAGGABLE_MYP_ELEMENTS_V2,
    ...TAGGABLE_UBD_ELEMENTS,
    ...TAGGABLE_PYP_ELEMENTS_V2,
  ];

  const fieldObj = _.find(allTaggableElements, { value: fieldKey }) || {};

  if (curriculumProgramType === CURRICULUM_TYPE_UBD) {
    return !_.isEmpty(plannerElementSets)
      ? plannerElementSets[pypElementMapping[fieldKey]]?.label ||
          t(_.get(fieldObj, "label", ""))
      : t(_.get(fieldObj, "label", ""));
  }

  return t(_.get(fieldObj, "label", ""));
};
